import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Check, ChevronDown, MailOpen, MessagesSquare, Trash2 } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import type { Ticket, TicketStatus } from '../../types';
import TicketSourceIcon from '../tickets/TicketSourceIcon';
import { getPriorityLabel, getStatusLabel } from '../../types';
import { getDeadlineStatus } from '../DeadlineBadge';
import SlaBadge from '../SlaBadge';
import Badge from '../ui/Badge';
import { priorityTone, statusTone, TONE_BADGE, TONE_DOT, TONE_TEXT } from '../ui/tone';
import { formatDate } from '../../utils/format';
import type { CardSpec, Column, RowAction } from '../DataTable';
import MenuButton, { useTouchMenus } from './MenuButton';

/**
 * Tickets list on DataTable v2 (PRD §6.2, T-39): the columns, the phone card
 * and the row actions. Compact single-line rows (Q-018b): the title is the
 * row link; the status badge stays visible and its change affordance appears
 * on row hover / focus (D5); delete lives in the row "⋯" (admin).
 */

export const TICKET_STATUSES: TicketStatus[] = ['planning', 'in_progress', 'in_review', 'testing', 'completed'];
/** D-146 — a service request's own statuses (utils/ticketStatus.shared.ts). */
export const SERVICE_TICKET_STATUSES: TicketStatus[] = ['new', 'in_progress', 'waiting_customer', 'completed', 'closed'];
/** D-154 (QX-85) — a discussion: "פתוח" / "סגור" (in_progress / completed). */
export const DISCUSSION_TICKET_STATUSES: TicketStatus[] = ['in_progress', 'completed'];
/** The statuses a row of this kind may be set to. */
export const statusesFor = (taskType?: string | null): TicketStatus[] =>
  (taskType === 'ticket' ? SERVICE_TICKET_STATUSES : taskType === 'discussion' ? DISCUSSION_TICKET_STATUSES : TICKET_STATUSES);

export function relativeDate(iso: string | undefined, t: TFunction): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  const diff = differenceInDays(day, today);
  if (diff === 0) return t('tickets.today');
  if (diff === 1) return t('tickets.tomorrow');
  if (diff === -1) return t('tickets.yesterday');
  return formatDate(d);
}

export function assigneeLabel(ticket: Ticket, t: TFunction): string {
  return ticket.agents && ticket.agents.length > 0
    ? ticket.agents.map((a) => a.name).join(', ')
    : ticket.agent_name || t('common.unassigned');
}

export function ticketNumber(ticket: Ticket): ReactNode {
  return ticket.ticket_number ? <bdi dir="ltr" className="whitespace-nowrap tabular-nums">TKT-{ticket.ticket_number}</bdi> : null;
}

function StatusBadge({ status, taskType }: { status: TicketStatus; taskType?: string | null }) {
  const { t } = useTranslation();
  return <Badge tone={statusTone(status)} dot>{getStatusLabel(status, t, taskType)}</Badge>;
}

/** The status badge; with permission it is also the trigger of the status menu (chevron on hover / focus). */
export function StatusCell({ ticket, status, canChange, onSelect }: { ticket: Ticket; status: TicketStatus; canChange: boolean; onSelect: (s: TicketStatus) => void }) {
  const { t } = useTranslation();
  const touch = useTouchMenus();
  // On touch the status changes from the row "⋯" sheet (one 44 px target per row).
  if (!canChange || touch) return <StatusBadge status={status} taskType={ticket.task_type} />;
  const tone = statusTone(status);
  const label = getStatusLabel(status, t, ticket.task_type);
  return (
    <MenuButton
      label={t('tickets.list.changeStatusOf', { status: label, title: ticket.title })}
      sheetTitle={t('uxTickets.changeStatus')}
      align="start"
      popoverClassName="w-52 p-1.5"
      triggerClassName={`dt-above inline-flex items-center gap-1.5 rounded-full py-0.5 pe-1.5 ps-2 text-xs font-medium whitespace-nowrap transition-shadow duration-micro hover:ring-1 hover:ring-control aria-expanded:ring-1 aria-expanded:ring-control ${TONE_BADGE[tone]}`}
      items={statusesFor(ticket.task_type).map((s) => ({
        key: s,
        label: getStatusLabel(s, t, ticket.task_type),
        textValue: getStatusLabel(s, t, ticket.task_type),
        icon: <span className={`size-2 rounded-full ${TONE_DOT[statusTone(s)]}`} />,
        trailing: s === status ? <Check size={16} strokeWidth={1.75} className="text-accent-text" aria-hidden="true" /> : undefined,
        onSelect: () => { if (s !== status) onSelect(s); },
      }))}
    >
      <span className={`size-1.5 shrink-0 rounded-full ${TONE_DOT[tone]}`} aria-hidden="true" />
      {label}
      <ChevronDown size={12} className="dt-reveal" aria-hidden="true" />
    </MenuButton>
  );
}

/** Priority: only urgent / high get a colour; medium / low stay quiet text. */
function PriorityLabel({ ticket }: { ticket: Ticket }) {
  const { t } = useTranslation();
  const tone = priorityTone(ticket.priority);
  const label = getPriorityLabel(ticket.priority, t);
  // C-32 (clarity, D-22): outlined, not filled, so a priority never reads as a status.
  if (tone !== 'neutral') return <Badge variant="outline" tone={tone}>{label}</Badge>;
  return <span className={`text-xs ${ticket.priority === 'low' ? 'text-text-3' : 'text-text-2'}`}>{label}</span>;
}

function DueLabel({ ticket }: { ticket: Ticket }) {
  const { t } = useTranslation();
  if (!ticket.due_date) return null;
  const s = getDeadlineStatus(ticket.due_date);
  const tone = s === 'overdue' ? 'danger' : s === 'approaching' ? 'warning' : 'neutral';
  return (
    <span className={`whitespace-nowrap text-xs tabular-nums ${tone === 'neutral' ? 'text-text-2' : `${TONE_TEXT[tone]} font-medium`}`}>
      {t('uxTickets.dueOn', { date: formatDate(ticket.due_date) })}
    </span>
  );
}

function Sla({ ticket, status }: { ticket: Ticket; status: TicketStatus }) {
  if (!ticket.sla_response_due_at && !ticket.sla_resolution_due_at) return null;
  return (
    <SlaBadge
      compact
      responseDueAt={ticket.sla_response_due_at}
      resolutionDueAt={ticket.sla_resolution_due_at}
      responseBreached={ticket.sla_response_breached}
      resolutionBreached={ticket.sla_resolution_breached}
      firstResponseAt={ticket.first_response_at}
      responseWarningSentAt={ticket.sla_response_warning_sent_at}
      resolutionWarningSentAt={ticket.sla_resolution_warning_sent_at}
      status={status}
    />
  );
}

/** Markers after the title: discussion, due date, SLA, cancelled. */
function TitleMarkers({ ticket, status }: { ticket: Ticket; status: TicketStatus }) {
  const { t } = useTranslation();
  return (
    <>
      <TicketSourceIcon source={ticket.source} />
      {ticket.task_type === 'discussion' && (
        <span className="inline-flex text-text-3" title={t(ticket.is_conversation ? 'discussions.tagConversation' : 'taskType.discussion')}>
          <MessagesSquare size={14} strokeWidth={1.75} aria-hidden="true" />
          <span className="sr-only">{t(ticket.is_conversation ? 'discussions.tagConversation' : 'taskType.discussion')}</span>
        </span>
      )}
      <DueLabel ticket={ticket} />
      <Sla ticket={ticket} status={status} />
      {ticket.cancelled_at && <Badge tone="neutral" title={ticket.cancel_reason || undefined}>{t('tickets.cancelledLabel')}</Badge>}
    </>
  );
}

interface TicketTableOptions {
  t: TFunction;
  canChangeStatus: boolean;
  /** Effective status (a close waiting out its undo window shows as completed). */
  statusOf: (ticket: Ticket) => TicketStatus;
  onStatusSelect: (ticket: Ticket, status: TicketStatus) => void;
}

export function ticketColumns({ t, canChangeStatus, statusOf, onStatusSelect }: TicketTableOptions): Column<Ticket>[] {
  return [
    {
      key: 'number',
      header: t('tickets.list.colNumber'),
      priority: 3,
      width: 'w-24',
      render: (ticket) => <span className="text-xs text-text-2">{ticketNumber(ticket)}</span>,
    },
    {
      key: 'title',
      header: t('uxTickets.colTicket'),
      link: true,
      render: (ticket) => (
        <bdi className={`${ticket.cancelled_at ? 'line-through decoration-text-disabled' : ''} ${ticket.is_unread ? 'font-semibold' : ''}`}>{ticket.title}</bdi>
      ),
      trail: (ticket) => <TitleMarkers ticket={ticket} status={statusOf(ticket)} />,
    },
    {
      key: 'customer',
      header: t('tickets.list.colCustomer'),
      priority: 3,
      width: 'w-40',
      render: (ticket) => (
        <span className="block max-w-40 truncate text-xs text-text-2" title={ticket.customer_name || undefined}>
          {ticket.customer_name || t('common.notSpecified')}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      width: 'w-36',
      render: (ticket) => (
        <StatusCell ticket={ticket} status={statusOf(ticket)} canChange={canChangeStatus} onSelect={(s) => onStatusSelect(ticket, s)} />
      ),
    },
    {
      key: 'priority',
      header: t('common.priority'),
      width: 'w-24',
      render: (ticket) => <PriorityLabel ticket={ticket} />,
    },
    {
      key: 'assignee',
      header: t('uxTickets.colAssignee'),
      priority: 2,
      width: 'w-36',
      render: (ticket) => (
        <span className="block max-w-36 truncate text-xs text-text-2" title={assigneeLabel(ticket, t)}>{assigneeLabel(ticket, t)}</span>
      ),
    },
    {
      key: 'opened',
      header: t('uxTickets.colOpened'),
      align: 'end',
      width: 'w-24',
      render: (ticket) => <span className="whitespace-nowrap text-xs text-text-2">{relativeDate(ticket.opened_at || ticket.created_at, t)}</span>,
    },
  ];
}

/** Phone card (PRD §6.2): title + age · status / priority / SLA · number · customer · assignee. */
export function ticketCard(
  ticket: Ticket,
  status: TicketStatus,
  t: TFunction,
  opts: { canChangeStatus: boolean; onStatusSelect: (s: TicketStatus) => void },
): CardSpec {
  const tone = priorityTone(ticket.priority);
  const meta: ReactNode[] = [
    ticketNumber(ticket),
    ticket.customer_name || t('common.notSpecified'),
    `${t('uxTickets.colAssignee')}: ${assigneeLabel(ticket, t)}`,
  ].filter(Boolean);
  return {
    title: <bdi className={`${ticket.cancelled_at ? 'line-through decoration-text-disabled' : ''} ${ticket.is_unread ? 'font-semibold' : ''}`}>{ticket.title}</bdi>,
    end: relativeDate(ticket.opened_at || ticket.created_at, t),
    badges: (
      <>
        <StatusCell ticket={ticket} status={status} canChange={opts.canChangeStatus} onSelect={opts.onStatusSelect} />
        {tone !== 'neutral' && <Badge tone={tone}>{getPriorityLabel(ticket.priority, t)}</Badge>}
        <Sla ticket={ticket} status={status} />
        {ticket.cancelled_at && <Badge tone="neutral">{t('tickets.cancelledLabel')}</Badge>}
        {ticket.task_type === 'discussion' && <Badge tone="neutral">{t(ticket.is_conversation ? 'discussions.tagConversation' : 'taskType.discussion')}</Badge>}
        <DueLabel ticket={ticket} />
      </>
    ),
    metaItems: meta,
  };
}

export function ticketRowActions(
  ticket: Ticket,
  status: TicketStatus,
  opts: { t: TFunction; canChangeStatus: boolean; canDelete: boolean; onStatusSelect: (s: TicketStatus) => void; onDelete: () => void; onMarkUnread?: () => void },
): RowAction[] {
  const { t } = opts;
  const actions: RowAction[] = [];
  if (opts.canChangeStatus) {
    statusesFor(ticket.task_type).forEach((s) => actions.push({
      key: `status-${s}`,
      label: getStatusLabel(s, t, ticket.task_type),
      textValue: getStatusLabel(s, t, ticket.task_type),
      icon: <span className={`size-2 rounded-full ${TONE_DOT[statusTone(s)]}`} />,
      trailing: s === status ? <Check size={16} strokeWidth={1.75} className="text-accent-text" aria-hidden="true" /> : undefined,
      touchOnly: true,
      onSelect: () => { if (s !== status) opts.onStatusSelect(s); },
    }));
  }
  // P-39 (D-199): "סמן כלא נקרא" - personal to the caller; hidden when already unread.
  if (opts.onMarkUnread && !ticket.is_unread) {
    actions.push({
      key: 'mark-unread',
      label: t('tickets.list.markUnread'),
      textValue: t('tickets.list.markUnread'),
      icon: <MailOpen size={16} strokeWidth={1.75} />,
      separatorBefore: actions.length > 0,
      onSelect: opts.onMarkUnread,
    });
  }
  if (opts.canDelete) {
    actions.push({
      key: 'delete',
      label: t('tickets.list.delete'),
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      danger: true,
      separatorBefore: actions.length > 0,
      onSelect: opts.onDelete,
      data: { 'data-ticket-delete': ticket.id },
    });
  }
  return actions;
}
